import { useEffect } from 'react'

import { joiResolver } from '@hookform/resolvers/joi'
import { LoadingButton } from '@mui/lab'
import { Box, Grid, Paper, Tooltip } from '@mui/material'
import Joi from 'joi'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import { CardTitle, ControlledDateTimePicker } from 'components/common'
import { JoiValidationOptions } from 'constants/index'
import { GetBookingNextAddressEtaDocument, useSubmitProvidedEtaMutation } from 'generated/graphql'
import { Restricted } from 'providers'

interface IProps {
  variables: {
    bookingId?: string
    sequenceOrder: number
    bookingAddressId?: string
    eTA?: Date
  }
  hoverText: string
  disabled?: boolean
  loading?: boolean
}

type EtaFormData = {
  eTA: Date | null
}

const validationSchema = Joi.object<EtaFormData>({
  eTA: Joi.date()
    .empty(null)
    .optional()
    .min('now')
    .messages({
      'date.min': 'ETA must be greater than current date and time',
    })
    .label('ETA'),
})

const PoAddressEta = (props: IProps) => {
  const { variables, disabled, hoverText, loading } = props
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<EtaFormData>({
    defaultValues: {
      eTA: null,
    },
    resolver: joiResolver(validationSchema, JoiValidationOptions),
    shouldUnregister: true,
  })

  // effects
  useEffect(() => {
    reset({ eTA: variables?.eTA ?? null })
  }, [variables.eTA, reset])

  // mutations
  const [submitProvidedEta, { loading: updatingEta }] = useSubmitProvidedEtaMutation({
    refetchQueries: [
      {
        query: GetBookingNextAddressEtaDocument,
        variables: { bookingId: Number(variables?.bookingId) },
      },
    ],
    onCompleted: (response, options) => {
      const updatedAddress = response.submitProvidedEta.find(
        (ba) => ba.id === options?.variables?.input?.bookingAddressId,
      )

      if (!updatedAddress) {
        toast.error('Something went wrong')
        return
      }

      const newEta = new Date(updatedAddress.eTA)
      toast.success(`Eta updated successfully ${newEta.toUTCString()}`)
    },
  })

  // handlers
  const handleAutoCalculateClick = () => {
    if (!variables.bookingAddressId) {
      return
    }
    submitProvidedEta({
      variables: {
        input: {
          calculateAutomatically: true,
          bookingAddressId: variables.bookingAddressId,
        },
      },
    })
  }

  const handleUpdateEta = (values: EtaFormData) => {
    if (!variables.bookingAddressId) {
      return
    }
    submitProvidedEta({
      variables: {
        input: {
          bookingAddressId: variables.bookingAddressId,
          calculateAutomatically: false,
          eta: values.eTA || new Date(),
        },
      },
    })
  }

  return (
    <Paper variant='outlined'>
      <Box padding={2}>
        <CardTitle>Estimated time of arrival {loading ? 'Loading ...' : ''}</CardTitle>

        <Tooltip title={disabled ? hoverText : ''} followCursor>
          <Grid container>
            <Grid item xs={12} sm={5}>
              <ControlledDateTimePicker
                control={control}
                name='eTA'
                defaultValue={null}
                label='ETA'
                disabled={disabled}
                disablePast
                minDateTime={new Date()}
                error={!!errors.eTA}
                helperText={errors.eTA?.message}
              />
            </Grid>

            <Restricted to='UpdateEta'>
              <Grid xs={12} sm={3}>
                {variables.sequenceOrder !== 0 && (
                  <LoadingButton
                    loading={updatingEta}
                    disabled={loading}
                    onClick={handleSubmit(handleAutoCalculateClick)}
                    size='large'
                    variant='outlined'
                    style={{ height: '100%' }}>
                    Auto
                  </LoadingButton>
                )}
              </Grid>

              <Grid xs={12} sm={3}>
                <LoadingButton
                  loading={updatingEta}
                  disabled={loading}
                  onClick={handleSubmit(handleUpdateEta)}
                  size='large'
                  variant='outlined'
                  style={{ height: '100%' }}>
                  Save
                </LoadingButton>
              </Grid>
            </Restricted>
          </Grid>
        </Tooltip>
      </Box>
    </Paper>
  )
}

export default PoAddressEta
